import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldCheck, ShieldAlert, ArrowLeft, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Security page — enable / disable 2FA, view status, see remaining backup codes.
 * Accessible from Settings → Sécurité.
 */
export default function SecuritySettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null); // { is_admin, totp_enabled, backup_codes_remaining, required }
  const [disableOpen, setDisableOpen] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/2fa/status");
      setStatus(data);
    } catch {
      toast.error("Impossible de charger l'état 2FA");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const disable2FA = async (e) => {
    e?.preventDefault?.();
    setSubmitting(true);
    try {
      await api.post("/auth/2fa/disable", { code: disableCode.trim() });
      toast.success("2FA désactivée");
      setDisableOpen(false);
      setDisableCode("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Code incorrect");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppShell>
      <div className="px-5 pt-6 pb-2 flex items-center gap-3">
        <button onClick={() => navigate("/settings")} className="w-9 h-9 rounded-full border border-[var(--border)] grid place-items-center hover:bg-black/5" data-testid="security-back">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-[var(--text)] tracking-tight leading-[0.95]" data-testid="security-title">
          Sécurité
        </h1>
      </div>

      <div className="px-5 mt-8 max-w-2xl">
        {loading && (
          <div className="text-sm text-zinc-400">Chargement…</div>
        )}

        {!loading && status && (
          <>
            {/* Status card */}
            <div className={`border ${status.totp_enabled ? "border-emerald-500/40" : "border-[var(--border)]"} p-6 rounded-2xl bg-[var(--surface)]`} data-testid="security-2fa-card">
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-full grid place-items-center shrink-0 ${status.totp_enabled ? "bg-black text-white" : "bg-[var(--surface-alt)] text-zinc-400"}`}>
                  {status.totp_enabled ? <ShieldCheck className="w-5 h-5" /> : <ShieldAlert className="w-5 h-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display font-bold text-xl" data-testid="security-2fa-title">
                      Double authentification (2FA)
                    </div>
                    {status.totp_enabled && (
                      <span className="text-[10px] font-bold uppercase tracking-widest bg-black text-white px-2 py-0.5 rounded-full" data-testid="security-2fa-state">Active</span>
                    )}
                    {!status.totp_enabled && (
                      <span className="text-[10px] font-bold uppercase tracking-widest border border-[var(--border)] px-2 py-0.5 rounded-full" data-testid="security-2fa-state">Désactivée</span>
                    )}
                  </div>
                  <p className="mt-2 text-sm text-zinc-300 leading-relaxed">
                    Protégez votre compte avec un code à 6 chiffres généré par Google Authenticator, Authy ou 1Password.
                  </p>
                  {status.is_admin && (
                    <div className="mt-3 flex items-start gap-2 bg-[var(--surface-alt)] border border-[var(--border)] rounded-lg px-3 py-2">
                      <ShieldCheck className="w-4 h-4 text-[var(--text)] shrink-0 mt-0.5" />
                      <p className="text-xs text-zinc-300 leading-relaxed">
                        <strong>Compte administrateur :</strong> la 2FA est optionnelle mais fortement recommandée pour protéger l'accès au panneau d'administration.
                      </p>
                    </div>
                  )}
                  {status.totp_enabled && (
                    <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-zinc-400">
                      <KeyRound className="w-3.5 h-3.5" />
                      {status.backup_codes_remaining} code{status.backup_codes_remaining > 1 ? "s" : ""} de secours restant{status.backup_codes_remaining > 1 ? "s" : ""}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6 flex gap-3 flex-wrap">
                {!status.totp_enabled ? (
                  <Button
                    onClick={() => navigate("/2fa-setup")}
                    className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white border border-emerald-500 h-11 px-5 text-sm font-medium"
                    data-testid="security-enable-2fa"
                  >
                    Activer la 2FA
                  </Button>
                ) : (
                  <>
                    <Button
                      onClick={() => setDisableOpen(true)}
                      variant="outline"
                      className="rounded-full border-[var(--border)] hover:bg-emerald-500 hover:text-white hover:border-emerald-500 h-11 px-5 text-sm font-medium"
                      data-testid="security-disable-2fa"
                    >
                      Désactiver
                    </Button>
                    <Button
                      onClick={() => navigate("/2fa-setup")}
                      variant="outline"
                      className="rounded-full border-[var(--border)] hover:bg-emerald-500 hover:text-white hover:border-emerald-500 h-11 px-5 text-sm font-medium"
                      data-testid="security-regen-2fa"
                    >
                      Régénérer (QR + codes)
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Disable form */}
            {disableOpen && (
              <div className="mt-5 border border-[var(--border)] p-6 rounded-2xl" data-testid="security-disable-form">
                <div className="font-display font-bold text-lg">Confirmer la désactivation</div>
                <p className="mt-2 text-sm text-zinc-300">
                  Entrez un code à 6 chiffres depuis votre application d'authentification, ou un code de secours à 8 caractères.
                </p>
                <form onSubmit={disable2FA} className="mt-4 flex gap-2 items-stretch max-w-sm">
                  <Input
                    autoFocus
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value.replace(/\s/g, ""))}
                    placeholder="123 456 ou ABCD1234"
                    maxLength={8}
                    data-testid="security-disable-code"
                    className="h-12 rounded-none border-0 border-b border-[var(--border)] focus-visible:border-black focus-visible:ring-0 px-0 text-base font-mono tracking-wider flex-1"
                  />
                  <Button
                    type="submit"
                    disabled={submitting || disableCode.length < 6}
                    className="rounded-full bg-black hover:bg-black/85 text-white h-12 px-5 text-sm"
                    data-testid="security-disable-submit"
                  >
                    {submitting ? "…" : "Confirmer"}
                  </Button>
                </form>
                <button
                  type="button"
                  onClick={() => { setDisableOpen(false); setDisableCode(""); }}
                  className="mt-3 text-xs text-zinc-400 hover:text-[var(--text)] underline underline-offset-4"
                >
                  Annuler
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}
